import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPortalRmaDetail } from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/rmas/$rmaId")({
  component: RmaDetail,
});

function RmaDetail() {
  const { rmaId } = Route.useParams();
  const fetchDetail = useServerFn(getPortalRmaDetail);
  const { data, isLoading, error } = useQuery({
    queryKey: ["portal-rma", rmaId],
    queryFn: () => runServerFn(() => fetchDetail({ data: { rmaId } }), "Loading RMA"),
  });

  return (
    <>
      <PortalPageHeader
        title={data?.rma?.rma_number ?? "RMA"}
        description="Return merchandise authorization"
        actions={
          <Button asChild size="sm" variant="outline">
            <Link to="/portal/rmas"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link>
          </Button>
        }
      />
      <div className="page-container space-y-4">
        {isLoading ? <p className="text-muted-foreground">Loading…</p>
        : error ? <p className="text-destructive">{(error as Error).message}</p>
        : data ? (
          <>
            <Card className="p-4 grid sm:grid-cols-4 gap-4 text-sm">
              <div><div className="text-muted-foreground text-xs">RMA #</div><div className="font-mono">{data.rma.rma_number}</div></div>
              <div><div className="text-muted-foreground text-xs">Status</div><StatusPill tone={statusTone(data.rma.status)}>{data.rma.status}</StatusPill></div>
              <div><div className="text-muted-foreground text-xs">Created</div><div>{new Date(data.rma.created_at).toLocaleDateString()}</div></div>
              <div className="sm:col-span-4"><div className="text-muted-foreground text-xs">Reason</div><div>{data.rma.reason ?? "—"}</div></div>
            </Card>
            <div className="border rounded-lg bg-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>SKU</TableHead><TableHead>Name</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>State</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.lines.length > 0 ? data.lines.map((l: any) => (
                    <TableRow key={l.id}>
                      <TableCell className="font-mono">{l.skus?.sku_code ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{l.skus?.name ?? "—"}</TableCell>
                      <TableCell className="text-right">{l.qty_expected}</TableCell>
                      <TableCell className="text-right">{l.qty_received}</TableCell>
                      <TableCell className="text-muted-foreground">{l.condition}</TableCell>
                      <TableCell className="text-muted-foreground">{l.return_state}</TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No lines on this RMA.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}